import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Download, X, Loader2, Trophy, Flame } from "lucide-react";
import { toast } from "sonner";
import { DIM_STYLE } from "@/components/Badges";

function Card({ innerRef, arch, dimension, streak, target = 7 }) {
  const accent = DIM_STYLE[dimension]?.c || "#D4AF37";
  return (
    <div ref={innerRef} style={{ width: 360, height: 480 }} className="relative overflow-hidden bg-[#09090B]">
      <div style={{ background: `radial-gradient(circle at 50% 0%, ${accent}22, transparent 60%)` }} className="absolute inset-0" />
      <div className="absolute -bottom-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-[#D4AF37]/12 blur-3xl" />
      <div className="relative flex h-full flex-col items-center p-8 text-center">
        <img src="/logo-horizontal.png" alt="Coach2Go" crossOrigin="anonymous" className="h-8 w-auto object-contain" />

        <div className="mt-auto flex flex-col items-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-[#D4AF37]/60 bg-gradient-to-br from-[#E5C158] to-[#997A20] text-[#09090B] shadow-[0_0_40px_rgba(212,175,55,0.35)]">
            <Trophy size={38} strokeWidth={1.8} />
          </div>
          <p style={{ color: "#D4AF37", letterSpacing: "0.28em" }} className="mt-6 text-[11px] font-semibold uppercase">Traguardo raggiunto</p>
          <h1 className="font-serif-el mt-3 text-[30px] font-semibold leading-[1.1] text-[#FAF9F6]">Percorso di 7 giorni<br />completato</h1>
          <p className="font-serif-el mt-3 text-lg italic text-[#D4D4D8]">{arch.title}</p>
        </div>

        <div className="mt-auto flex w-full items-center justify-center gap-6 border-t border-[#D4AF37]/25 pt-4">
          <div className="flex items-center gap-1.5">
            <span className="font-mono-el text-2xl font-semibold text-[#FAF9F6]">{target}/7</span>
            <span className="text-[12px] uppercase tracking-wider text-[#8A8E96]">giorni</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Flame size={18} className="text-[#D4AF37]" />
            <span className="font-mono-el text-2xl font-semibold text-[#FAF9F6]">{streak}</span>
            <span className="text-[12px] uppercase tracking-wider text-[#8A8E96]">vissuti</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ShareAchievement({ arch, dimension, streak, variant = "gold" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  const render = async () => {
    if (document.fonts?.ready) await document.fonts.ready;
    await new Promise((r) => setTimeout(r, 150));
    return toPng(cardRef.current, { pixelRatio: 3, cacheBust: true, backgroundColor: "#09090B" });
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = await render();
      const a = document.createElement("a");
      a.href = url;
      a.download = "coach2go-traguardo-7-giorni.png";
      a.click();
      toast.success("Immagine scaricata");
    } catch {
      toast.error("Impossibile generare l'immagine");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const url = await render();
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], "coach2go-traguardo-7-giorni.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Traguardo Coach2Go", text: "Ho completato il mio percorso di 7 giorni su Coach2Go! 🏆" });
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        toast.success("Immagine scaricata (condivisione non supportata qui)");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Condivisione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="share-achievement-button"
        className={variant === "gold"
          ? "btn-gold inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm uppercase tracking-wide"
          : "btn-outline-gold inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm"}
      >
        <Share2 size={16} /> Condividi il traguardo
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-5 backdrop-blur-sm"
              onClick={() => !busy && setOpen(false)}
              data-testid="achievement-modal"
            >
              <motion.div
                initial={{ scale: 0.92, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.92, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-[360px]"
              >
                <div className="flex justify-end">
                  <button onClick={() => setOpen(false)} data-testid="achievement-close" className="mb-2 flex h-9 w-9 items-center justify-center rounded-full border border-[#27272A] bg-[#121215] text-[#B4B4BC]">
                    <X size={18} />
                  </button>
                </div>
                <div className="overflow-hidden rounded-2xl border border-[#27272A] shadow-2xl">
                  <Card innerRef={cardRef} arch={arch} dimension={dimension} streak={streak} />
                </div>
                <div className="mt-4 flex gap-3">
                  <button onClick={share} disabled={busy} data-testid="achievement-share-do" className="btn-gold flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm uppercase tracking-wide">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Condividi
                  </button>
                  <button onClick={download} disabled={busy} data-testid="achievement-download" className="btn-outline-gold flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm">
                    <Download size={16} /> Scarica
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
